import React, { useMemo, useCallback } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ScatterChart,
  Scatter,
} from 'recharts';
import { UserActionColors } from './userActionEnum.js';
import {
  formatDurationMinutes,
  buildActivityOverTimeSeries,
  buildUserActivityOverTimeSeries,
  buildUserActivityByStaffSeries,
  getReadyForReviewSeries,
  buildDocumentIntakeSeries,
  buildDocumentProcessingPoints,
  buildConsultationProcessingPoints,
  formatDateTimeForTooltip,
  DOCUMENT_PROCESSING_METRIC_LABELS,
  CONSULTATION_PROCESSING_METRIC_LABELS,
} from './chartSeries.js';

export { parseDuration, formatDurationMinutes, formatDateTimeForTooltip } from './chartSeries.js';

// Create a smart date formatter that adapts based on the date range
const createDateFormatter = (data) => {
  if (!data || data.length === 0) {
    return (isoString) => isoString;
  }

  // Get the date range of the data
  const timestamps = data.map(d => new Date(d.timestamp || d));
  const minDate = new Date(Math.min(...timestamps));
  const maxDate = new Date(Math.max(...timestamps));
  
  // Calculate the span in hours
  const hourSpan = (maxDate - minDate) / (1000 * 60 * 60);
  
  // If data window is <= 24 hours, show time only
  if (hourSpan <= 24) {
    return (isoString) => {
      try {
        return new Date(isoString).toLocaleTimeString('en-GB', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false
        });
      } catch (error) {
        return isoString;
      }
    };
  }
  
  // For time windows > 24 hours, show date only
  return (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit'
      });
    } catch (error) {
      return isoString;
    }
  };
};

export const formatSnapshotTimestamp = (isoString) => {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    
    // If within the last hour, show relative time
    if (diffMinutes < 60 && diffMinutes > 0) {
      return `${diffMinutes}m ago`;
    } else if (diffMinutes === 0) {
      return 'Now';
    }
    
    // Otherwise, use the original format
    const month = date.toLocaleDateString('en-GB', { month: 'long' });
    const day = date.getDate();
    const hour = date.toLocaleTimeString('en-GB', { 
      hour: 'numeric', 
      hour12: true 
    }).toLowerCase().replace(/\s+(am|pm)/, '$1');
    return `${month} ${day} ${hour}`;
  } catch (error) {
    return 'Current snapshot';
  }
};

export const formatDateRange = (data) => {
  if (!data || data.length === 0) return '';
  
  const firstTimestamp = new Date(data[0].timestamp);
  const lastTimestamp = new Date(data[data.length - 1].timestamp);
  
  const formatDateTime = (date) => {
    const time = date.toLocaleTimeString('en-GB', {
      hour: 'numeric',
      hour12: true
    }).toLowerCase().replace(/\s+(am|pm)/, '$1');
    const dateStr = date.toLocaleDateString('en-GB', { 
      month: 'long', 
      day: 'numeric' 
    });
    return { time, date: dateStr, fullDate: date };
  };
  
  const start = formatDateTime(firstTimestamp);
  const end = formatDateTime(lastTimestamp);
  
  // Check if same day
  if (start.fullDate.toDateString() === end.fullDate.toDateString()) {
    return `${start.date}, ${start.time} to ${end.time}`;
  } else {
    return `${start.time} ${start.date} to ${end.time} ${end.date}`;
  }
};

export const DocumentsReadyForReviewChart = React.memo(({ data, type }) => {
  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  const formatDate = useMemo(() => createDateFormatter(data || []), [data]);
  const dateRangeText = useMemo(() => formatDateRange(data || []), [data]);
  const series = useMemo(() => getReadyForReviewSeries(data, type), [data, type]);

  // NOW safe to do early returns - all hooks have been called
  if (!data || data.length === 0) {
    return <div className="no-data">No data available</div>;
  }

  return (
    <div>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} isAnimationActive={false}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="timestamp" tickFormatter={formatDate} />
          <YAxis />
          <Tooltip labelFormatter={formatDateTimeForTooltip} />
          <Legend />
          {series.map(item => (
            <Bar
              key={item.dataKey}
              dataKey={item.dataKey}
              stackId="a"
              name={item.name}
              fill={item.fill}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <div className="chart-subtitle">{dateRangeText}</div>
    </div>
  );
});

export const DocumentActivityChart = React.memo(({ data, allUserActions, userActionLabels, activeActions, onToggleAction, filteredUserIds, aggregationMode = 'cumulative' }) => {
  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS

  const chartData = useMemo(
    () => buildActivityOverTimeSeries(data, allUserActions, filteredUserIds, aggregationMode),
    [data, allUserActions, filteredUserIds, aggregationMode]
  );

  const formatDate = useMemo(() => createDateFormatter(data || []), [data]);
  const dateRangeText = useMemo(() => formatDateRange(data || []), [data]);

  // Handle legend click (Recharts default legend)
  const handleLegendClick = useCallback((e) => {
    if (e && e.dataKey) {
      onToggleAction(e.dataKey);
    }
  }, [onToggleAction]);

  const isIndividual = aggregationMode === 'individual';

  // NOW safe to do early returns - all hooks have been called
  if (!data || data.length === 0) {
    return <div className="no-data">No data available</div>;
  }

  const series = allUserActions.map((action) => {
    const isActive = activeActions.includes(action);
    const name = userActionLabels[action] || action;
    const color = UserActionColors[action] || '#8884d8';
    if (isIndividual) {
      return (
        <Bar
          key={action}
          dataKey={action}
          name={name}
          stackId="activity"
          fill={color}
          opacity={isActive ? 1 : 0.15}
          hide={!isActive}
          isAnimationActive={false}
        />
      );
    }
    return (
      <Line
        key={action}
        type="monotone"
        dataKey={action}
        name={name}
        stroke={color}
        strokeWidth={2}
        dot={{ r: 3 }}
        opacity={isActive ? 1 : 0.15}
        hide={!isActive}
        isAnimationActive={false}
      />
    );
  });

  const chart = isIndividual ? (
    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} isAnimationActive={false}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="timestamp" tickFormatter={formatDate} />
      <YAxis allowDecimals={false} />
      <Tooltip labelFormatter={formatDateTimeForTooltip} />
      <Legend />
      {series}
    </BarChart>
  ) : (
    <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} isAnimationActive={false}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="timestamp" tickFormatter={formatDate} />
      <YAxis />
      <Tooltip labelFormatter={formatDateTimeForTooltip} />
      <Legend />
      {series}
    </LineChart>
  );

  return (
    <div>
      <ResponsiveContainer width="100%" height={300}>
        {chart}
      </ResponsiveContainer>
      <div className="chart-subtitle">{dateRangeText}</div>
    </div>
  );
});

export const BacklogChart = React.memo(({ data }) => {
  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  const formatDate = useMemo(() => createDateFormatter(data || []), [data]);
  const dateRangeText = useMemo(() => formatDateRange(data || []), [data]);

  // NOW safe to do early returns - all hooks have been called
  if (!data || data.length === 0) {
    return <div className="no-data">No data available</div>;
  }

  return (
    <div>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} isAnimationActive={false}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="timestamp" tickFormatter={formatDate} />
          <YAxis />
          <Tooltip labelFormatter={formatDateTimeForTooltip} />
          <Legend />
          <Line type="monotone" dataKey="storageSnapshot.documentsNeverOpened" name="Never Opened" stroke="#ffc658" strokeWidth={2} isAnimationActive={false} />
          <Line type="monotone" dataKey="storageSnapshot.documentsReadyForReview" name="Ready for Review" stroke="#82ca9d" strokeWidth={2} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
      <div className="chart-subtitle">{dateRangeText}</div>
    </div>
  );
});

export const AssignedSummaryPieChart = React.memo(({ data, timestamp }) => {
  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  const COLORS = useMemo(() => ['#8884d8', '#d0d0d0'], []);
  const timestampText = useMemo(() => 
    timestamp ? formatSnapshotTimestamp(timestamp) : 'Now',
    [timestamp]
  );

  // NOW safe to do early returns - all hooks have been called
  if (!data || data.length === 0) {
    return <div className="no-data">No data available</div>;
  }
  
  return (
    <div>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie 
            data={data} 
            dataKey="value" 
            nameKey="name" 
            cx="50%" 
            cy="50%" 
            outerRadius={120} 
            innerRadius={60}
            isAnimationActive={false}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
      <div className="chart-subtitle">{timestampText}</div>
    </div>
  );
});

export const DocumentCreationSourcePieChart = React.memo(({ data }) => {
  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  const COLORS = useMemo(() => ['#3498db', '#2ecc71'], []); // Blue for received, Green for uploaded
  const chartData = useMemo(() => buildDocumentIntakeSeries(data), [data]);
  
  const dateRangeText = useMemo(() => formatDateRange(data || []), [data]);
  const totalCreates = useMemo(() => 
    chartData.reduce((sum, item) => sum + item.value, 0),
    [chartData]
  );

  // NOW safe to do early returns - all hooks have been called
  if (!data || data.length === 0 || totalCreates === 0) {
    return <div className="no-data">MedPrompt did not receive any documents in this period</div>;
  }
  
  return (
    <div>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie 
            data={chartData} 
            dataKey="value" 
            nameKey="name" 
            cx="50%" 
            cy="50%" 
            outerRadius={120} 
            innerRadius={60}
            isAnimationActive={false}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
      <div className="chart-subtitle">{dateRangeText}</div>
    </div>
  );
});

export const UserActivityOverTimeChart = React.memo(({ data, selectedAction, userIdToName = {}, filteredUserIds, activeUsers, onToggleUser, aggregationMode = 'cumulative' }) => {
  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS

  const series = useMemo(
    () => buildUserActivityOverTimeSeries({
      data,
      selectedAction,
      userIdToName,
      filteredUserIds,
      mode: aggregationMode,
    }),
    [data, selectedAction, userIdToName, filteredUserIds, aggregationMode]
  );
  const chartData = series.rows;
  const userList = series.userNames;

  const formatDate = useMemo(() => createDateFormatter(data || []), [data]);
  const dateRangeText = useMemo(() => formatDateRange(data || []), [data]);

  // Define colors for users (cycling through a palette)
  const userColors = useMemo(() => [
    '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1',
    '#d084d0', '#ffb347', '#87ceeb', '#daa520', '#98fb98'
  ], []);

  // Handle legend click
  const handleLegendClick = useCallback((e) => {
    if (e && e.dataKey && onToggleUser) {
      onToggleUser(e.dataKey);
    }
  }, [onToggleUser]);

  // NOW safe to do early returns - all hooks have been called
  if (!data || data.length === 0) {
    return <div className="no-data">No data available</div>;
  }

  if (userList.length === 0) {
    return <div className="no-data">No user activity data available for this action</div>;
  }

  const isIndividual = aggregationMode === 'individual';
  const plottedSeries = userList.map((userName, index) => {
    const isActive = !activeUsers || activeUsers.includes(userName);
    const color = userColors[index % userColors.length];
    if (isIndividual) {
      return (
        <Bar
          key={userName}
          dataKey={userName}
          name={userName}
          stackId="staff"
          fill={color}
          opacity={isActive ? 1 : 0.15}
          hide={!isActive}
          isAnimationActive={false}
        />
      );
    }
    return (
      <Line
        key={userName}
        type="monotone"
        dataKey={userName}
        name={userName}
        stroke={color}
        strokeWidth={2}
        dot={{ r: 3 }}
        opacity={isActive ? 1 : 0.15}
        hide={!isActive}
        isAnimationActive={false}
      />
    );
  });

  const chart = isIndividual ? (
    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} isAnimationActive={false}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="timestamp" tickFormatter={formatDate} />
      <YAxis allowDecimals={false} />
      <Tooltip labelFormatter={formatDateTimeForTooltip} />
      <Legend onClick={handleLegendClick} />
      {plottedSeries}
    </BarChart>
  ) : (
    <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} isAnimationActive={false}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="timestamp" tickFormatter={formatDate} />
      <YAxis />
      <Tooltip labelFormatter={formatDateTimeForTooltip} />
      <Legend onClick={handleLegendClick} />
      {plottedSeries}
    </LineChart>
  );

  return (
    <div>
      <ResponsiveContainer width="100%" height={300}>
        {chart}
      </ResponsiveContainer>
      <div className="chart-subtitle">{dateRangeText}</div>
    </div>
  );
});

export const UserActivityByStaffChart = React.memo(({ data, selectedAction, userIdToName = {}, filteredUserIds }) => {
  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  
  const chartData = useMemo(
    () => buildUserActivityByStaffSeries({ data, selectedAction, userIdToName, filteredUserIds }),
    [data, selectedAction, userIdToName, filteredUserIds]
  );

  const dateRangeText = useMemo(() => formatDateRange(data || []), [data]);

  // Get color for selected action - MATCHES UserAction color for visual clarity
  const barColor = useMemo(() => 
    UserActionColors[selectedAction] || '#8884d8',
    [selectedAction]
  );

  // NOW safe to do early returns - all hooks have been called
  if (!data || data.length === 0) {
    return <div className="no-data">No data available</div>;
  }

  if (chartData.length === 0) {
    return <div className="no-data">No user activity data available for this action</div>;
  }

  return (
    <div>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} isAnimationActive={false}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="userId" />
          <YAxis />
          <Tooltip />
          <Bar dataKey="total" name="Total Activity" fill={barColor} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
      <div className="chart-subtitle">{dateRangeText}</div>
    </div>
  );
});

export const DocumentProcessingTimesChart = React.memo(({ reviewData, selectedMetric, onPointClick, xAxisLabel }) => {
  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  
  // Transform data for scatter plot, filtering out null values - MEMOIZED
  const chartData = useMemo(
    () => buildDocumentProcessingPoints(reviewData, selectedMetric),
    [reviewData, selectedMetric]
  );

  const metricNames = DOCUMENT_PROCESSING_METRIC_LABELS;

  const colors = useMemo(() => ({
    timeToOpen: '#82ca9d',
    timeToReview: '#8884d8',
    timeToFileAway: '#ffc658'
  }), []);

  // Handle scatter point click - MEMOIZED
  const handleScatterClick = useCallback((data) => {
    if (data && data.documentId && onPointClick) {
      onPointClick(data.documentId);
    }
  }, [onPointClick]);

  // Create a formatter based on the timestamp range - MEMOIZED
  const formatDate = useMemo(() => {
    if (chartData.length === 0) return (ts) => ts;
    const timestampData = chartData.map(d => ({ timestamp: new Date(d.timestamp).toISOString() }));
    return createDateFormatter(timestampData);
  }, [chartData]);
  
  const formatXAxisTick = useCallback((timestamp) => {
    // timestamp is in milliseconds, convert to ISO string for formatting
    return formatDate(new Date(timestamp).toISOString());
  }, [formatDate]);

  const formatYAxisTick = useCallback((value) => {
    return formatDurationMinutes(value);
  }, []);

  // Custom tooltip component - MEMOIZED
  const CustomTooltip = useCallback(({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="recharts-default-tooltip" style={{
          backgroundColor: 'rgb(255, 255, 255)',
          padding: '10px',
          border: '1px solid rgb(204, 204, 204)',
          borderRadius: '4px',
          zIndex: 1000
        }}>
          <p className="recharts-tooltip-label" style={{ margin: '0 0 5px 0' }}>
            {formatDateTimeForTooltip(new Date(data.timestamp).toISOString())}
          </p>
          <p className="recharts-tooltip-item" style={{ margin: 0, color: colors[selectedMetric] }}>
            {metricNames[selectedMetric]}: {formatDurationMinutes(data.duration)}
          </p>
          {data.documentId && onPointClick && (
            <p className="recharts-tooltip-item" style={{ margin: '5px 0 0 0', fontSize: '0.9em', color: '#666' }}>
              Click to view document
            </p>
          )}
        </div>
      );
    }
    return null;
  }, [selectedMetric, colors, metricNames, onPointClick]);

  // Only enable click behavior if onPointClick handler is provided - MEMOIZED
  const scatterProps = useMemo(() => {
    const props = {
      name: metricNames[selectedMetric],
      data: chartData,
      fill: colors[selectedMetric],
      isAnimationActive: false,
    };

    if (onPointClick) {
      props.onClick = handleScatterClick;
      props.cursor = "pointer";
    }

    return props;
  }, [metricNames, selectedMetric, chartData, colors, onPointClick, handleScatterClick]);

  // NOW safe to do early returns - all hooks have been called
  if (!reviewData || !reviewData.documentsReviewSummary || reviewData.documentsReviewSummary.length === 0) {
    return <div className="no-data">No data available</div>;
  }

  if (chartData.length === 0) {
    return <div className="no-data">No data available for selected metric</div>;
  }

  return (
    <div>
      <ResponsiveContainer width="100%" height={300}>
        <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 30 }} isAnimationActive={false}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="timestamp" 
            domain={['auto', 'auto']}
            name="Time"
            label={{ value: xAxisLabel || 'Time', position: 'insideBottom', offset: -5 }}
            tickFormatter={formatXAxisTick}
            type="number"
          />
          <YAxis 
            dataKey="duration" 
            name="Duration"
            tickFormatter={formatYAxisTick}
            type="number"
          />
          <Tooltip content={CustomTooltip} />
          <Scatter {...scatterProps} />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
});

export const ConsultationsReadyForReviewChart = React.memo(({ data }) => {
  const formatDate = useMemo(() => createDateFormatter(data || []), [data]);
  const dateRangeText = useMemo(() => formatDateRange(data || []), [data]);

  if (!data || data.length === 0) {
    return <div className="no-data">No data available</div>;
  }

  return (
    <div>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} isAnimationActive={false}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="timestamp" tickFormatter={formatDate} />
          <YAxis allowDecimals={false} />
          <Tooltip labelFormatter={formatDateTimeForTooltip} />
          <Legend />
          <Bar
            dataKey="storageSnapshot.consultationsReadyForReview"
            name="Ready for Review"
            fill="#16a085"
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
      <div className="chart-subtitle">{dateRangeText}</div>
    </div>
  );
});

export const ConsultationProcessingTimesChart = React.memo(({ reviewData, selectedMetric, xAxisLabel }) => {
  const chartData = useMemo(
    () => buildConsultationProcessingPoints(reviewData, selectedMetric),
    [reviewData, selectedMetric]
  );

  const metricNames = CONSULTATION_PROCESSING_METRIC_LABELS;

  const colors = useMemo(() => ({
    recordingDuration: '#16a085',
    timeToReview: '#8884d8',
  }), []);

  const formatDate = useMemo(() => {
    if (chartData.length === 0) return (ts) => ts;
    const timestampData = chartData.map(d => ({ timestamp: new Date(d.timestamp).toISOString() }));
    return createDateFormatter(timestampData);
  }, [chartData]);

  const formatXAxisTick = useCallback((timestamp) => {
    return formatDate(new Date(timestamp).toISOString());
  }, [formatDate]);

  const formatYAxisTick = useCallback((value) => {
    return formatDurationMinutes(value);
  }, []);

  const CustomTooltip = useCallback(({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      const point = payload[0].payload;
      return (
        <div className="recharts-default-tooltip" style={{
          backgroundColor: 'rgb(255, 255, 255)',
          padding: '10px',
          border: '1px solid rgb(204, 204, 204)',
          borderRadius: '4px',
          zIndex: 1000
        }}>
          <p className="recharts-tooltip-label" style={{ margin: '0 0 5px 0' }}>
            {formatDateTimeForTooltip(new Date(point.timestamp).toISOString())}
          </p>
          <p className="recharts-tooltip-item" style={{ margin: 0, color: colors[selectedMetric] }}>
            {metricNames[selectedMetric]}: {formatDurationMinutes(point.duration)}
          </p>
        </div>
      );
    }
    return null;
  }, [selectedMetric, colors, metricNames]);

  if (!reviewData || !reviewData.consultationsReviewSummary || reviewData.consultationsReviewSummary.length === 0) {
    return <div className="no-data">No data available</div>;
  }

  if (chartData.length === 0) {
    return <div className="no-data">No data available for selected metric</div>;
  }

  return (
    <div>
      <ResponsiveContainer width="100%" height={300}>
        <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 30 }} isAnimationActive={false}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="timestamp"
            domain={['auto', 'auto']}
            name="Time"
            label={{ value: xAxisLabel || 'Time', position: 'insideBottom', offset: -5 }}
            tickFormatter={formatXAxisTick}
            type="number"
          />
          <YAxis
            dataKey="duration"
            name="Duration"
            tickFormatter={formatYAxisTick}
            type="number"
          />
          <Tooltip content={CustomTooltip} />
          <Scatter
            name={metricNames[selectedMetric]}
            data={chartData}
            fill={colors[selectedMetric]}
            isAnimationActive={false}
          />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
});
